import { G, clamp, type Vec2, wrapIndex } from '../core/math';
import { SURFACES, TICK_DT, VEHICLE_HALF_WIDTH, type GripPatch, type Surface } from '../physics/constants';
import { createKart, speedOf, stepKart, type KartState } from '../physics/vehicle';
import { DEFAULT_REALISM, realismById, type RealismProfile } from '../physics/realism';
import { createTyres, stepTyres, tyreGrip, type TyreState } from '../physics/tyres';
import { CHECKPOINT_FRACTION, type Track } from '../track/generate';
import { clampToBarrier, type BarrierImpact, segmentCrossing, startLine, TrackQuery } from '../track/lookup';
import type { SpeedProfile } from '../track/speedprofile';
import type { Corner } from '../track/brakingzones';
import type { Weather } from '../track/weather';
import type { DrillResult, DrillSegment } from './drill';
import { analyseRun, type CornerDelta } from './analysis';
import { dequantise, dequantiseDrag, quantiseDrag, TRACE_STRIDE } from './controls';
import type { Controls } from '../physics/vehicle';

export type Phase = 'idle' | 'running' | 'finished';

/** Ghost samples are taken at 30 Hz and interpolated on playback. */
export const GHOST_STRIDE = 4;

/** Countdown before a drill releases. Long enough to read the corner ahead. */
export const HOLD_SECONDS = 1.6;

/** Yaw disturbance at full kerb contact and speed, rad/s — see the kerb
 *  rumble in `tick()`. */
const KERB_RUMBLE_GAIN = 0.22;

export interface Ghost {
  x: Float32Array;
  y: Float32Array;
  heading: Float32Array;
  /** Speed at each ghost frame, m/s. Drives the replay overlay and share card. */
  speed: Float32Array;
  /** Brake pressure 0..1 per frame, for the replay telemetry trace. */
  brake: Float32Array;
  /** Steering -1..1 per frame. */
  steer: Float32Array;
  /** Lateral acceleration in g. Positive is a left-hand load. */
  latG: Float32Array;
  /** Longitudinal acceleration in g. Negative under braking. */
  longG: Float32Array;
  /** 0..1 how much of each axle's grip was being used. */
  frontLoad: Float32Array;
  rearLoad: Float32Array;
  /** Nearest centerline index, so traces can be plotted against distance. */
  index: Int32Array;
  timeMs: number;
}

export interface RunResult {
  timeMs: number;
  valid: boolean;
  reason: string;
  checkpointsHit: number;
  checkpointsTotal: number;
  idealMs: number;
  /** Cumulative time at each of the three sector boundaries, ms. */
  sectorMs: number[];
  /** Cumulative time at each microsector boundary, ms — length
   *  {@link MICRO_COUNT}, last entry == {@link RunResult.timeMs}. */
  microMs: number[];
  deltas: CornerDelta[];
  worst: CornerDelta | null;
  ghost: Ghost;
  /** Two bytes per physics tick: steer (int8) then brake (uint8). */
  trace: Uint8Array;
  /** One byte per physics tick: quantised drag multiplier, additive to
   *  `trace` — see `quantiseDrag`. */
  dragTrace: Uint8Array;
  maxSpeed: number;
  /** True when the tyre model was on, so the ideal lap was only approximate. */
  tyreModel: boolean;
  /** Mid-lap respawns used. Costs time rather than validity. */
  respawns: number;
}

export interface SessionOptions {
  track: Track;
  profile: SpeedProfile;
  corners: Corner[];
  weather: Weather;
  query?: TrackQuery;
  realism?: RealismProfile;
  tyreModel?: boolean;
}

export class Session {
  readonly track: Track;
  readonly profile: SpeedProfile;
  readonly corners: Corner[];
  readonly query: TrackQuery;
  weather: Weather;
  realism: RealismProfile;
  tyreModel: boolean;

  readonly kart: KartState;
  readonly tyres: TyreState = createTyres();
  phase: Phase = 'idle';

  /**
   * Active corner drill, or null during a normal lap. A drill is its own kind of
   * run — it has a start, an end and a score — which the old boolean could not
   * express, and that is most of why the mode had no loop.
   */
  drill: DrillSegment | null = null;
  /** Ticks remaining on the pre-drill hold. Physics is frozen while it runs. */
  holdTicks = 0;
  drillResult: DrillResult | null = null;
  /** Best drill attempt this session, ms. Null until one is completed. */
  drillBestMs: number | null = null;
  drillAttempts = 0;

  get practice(): boolean {
    return this.drill !== null;
  }

  private readonly line: { a: Vec2; b: Vec2 };
  private readonly forward: Vec2;
  private readonly checkpointSet: Set<number>;
  private readonly controls: Controls = { steer: 0, throttle: 1, brake: 0, reverse: 0 };

  private trace: number[] = [];
  private dragTrace: number[] = [];
  private ticks = 0;
  private prevIndex = 0;
  private hitCheckpoints = new Set<number>();
  private sectorTicks: number[] = [];
  private nextSector = 1;
  private microTicks: number[] = [];
  private nextMicro = 1;
  private maxSpeed = 0;

  private speedAt: Float64Array;
  private lockedAt: Uint8Array;
  private offAt: Uint8Array;

  private ghostX: number[] = [];
  private ghostY: number[] = [];
  private ghostH: number[] = [];
  private ghostV: number[] = [];
  private ghostB: number[] = [];
  private ghostS: number[] = [];
  private ghostLatG: number[] = [];
  private ghostLongG: number[] = [];
  private ghostFront: number[] = [];
  private ghostRear: number[] = [];
  private ghostIdx: number[] = [];
  /** World velocity on the previous tick, for differentiating acceleration. */
  private prevVx = 0;
  private prevVy = 0;

  /** Most recent checkpoint passed, for mid-lap respawns. */
  private lastCheckpoint: number;
  /** Previous sample index, tracked separately for drill end detection. */
  private drillPrevIndex = 0;
  /** How many times the player has respawned this lap. */
  respawns = 0;

  result: RunResult | null = null;
  onTrack = true;
  currentIndex = 0;
  /** Grip actually under the kart this tick, for the HUD's wet warning. */
  currentMu = SURFACES.track.mu;
  /** 0..1 how much of the kart is on a kerb this tick, for audio/feedback. */
  currentKerb = 0;
  /** This tick's lateral g. Positive is a left-hand load. For the live HUD
   *  readout; the ghost's own trace is sampled from the same value. */
  liveLatG = 0;
  /** This tick's longitudinal g. Negative under braking. */
  liveLongG = 0;

  /** Barrier damage, 1 = pristine, 0 = wrecked. Resets every launch/reset.
   *  Damage only ever reduces from here; it is an orthogonal modifier on the
   *  physics so an undamaged lap is identical to the undamaged baseline. */
  damage = 1;
  /** Set on the ticks the barrier is touched, for FX/HUD. */
  impact: BarrierImpact | null = null;
  /** One-shot flag raised on a *damaging* hit, consumed by the renderer so an
   *  impact burst (sparks + thud) plays once rather than every scrape tick. */
  hitFlash = false;
  /** Running total of impact speed absorbed, for FX intensity. */
  private damageAccumulated = 0;

  constructor(opts: SessionOptions) {
    this.track = opts.track;
    this.profile = opts.profile;
    this.corners = opts.corners;
    this.weather = opts.weather;
    this.realism = opts.realism ?? realismById(DEFAULT_REALISM);
    this.tyreModel = opts.tyreModel ?? false;
    this.query = opts.query ?? new TrackQuery(opts.track);

    this.line = startLine(this.track);
    this.forward = {
      x: Math.cos(this.track.startHeading),
      y: Math.sin(this.track.startHeading),
    };
    this.checkpointSet = new Set(this.track.checkpoints);

    const n = this.track.center.length;
    this.speedAt = new Float64Array(n);
    this.lockedAt = new Uint8Array(n);
    this.offAt = new Uint8Array(n);

    const start = this.track.center[this.track.startIndex];
    this.kart = createKart(start.x, start.y, this.track.startHeading);
    this.prevIndex = this.track.startIndex;
    this.currentIndex = this.track.startIndex;
    this.lastCheckpoint = this.track.startIndex;
  }

  /**
   * Drop back in at the last checkpoint, carrying the speed the ideal lap would
   * have there.
   *
   * The clock keeps running, so a respawn always costs more time than it saves
   * on a good lap — which means it needs no special-casing for validity, and a
   * ruined run becomes a chance to keep practising the rest of the circuit
   * instead of a forced restart. Learning a track is much easier when a mistake
   * at the last corner does not throw away the three before it.
   */
  respawn(): void {
    if (this.phase !== 'running') return;
    this.respawns++;
    this.placeAt(this.lastCheckpoint, this.profile.vIdeal[this.lastCheckpoint] * 0.92);
  }

  /**
   * Barrier contact costs integrity. How much depends on how fast you were
   * going into the wall and how head-on it was — a perpendicular thud at speed
   * is far worse than catching a wall as you slide past. This only ever moves
   * `damage` down, and it is driven entirely by state that the replay
   * reproduces, so a damaged lap still re-simulates to the same outcome.
   */
  private applyBarrierDamage(impact: BarrierImpact): void {
    // Contact that merely redirected us (a graze, or a nudge at crawling pace)
    // is not damage. The threshold is deliberately forgiving at low speed.
    if (impact.headOn < 0.12 || impact.impactSpeed < 1.5) return;

    // Head-on weighting: 40..90% of the impact speed counts once we're past
    // the graze threshold, capped far enough that one hard hit halves you but
    // does not instantly end the run.
    const weight = 0.4 + impact.headOn * 0.5;
    const severity = impact.impactSpeed * weight;
    const loss = Math.min(0.35, severity * 0.012);

    this.damage = Math.max(0, this.damage - loss);
    this.damageAccumulated += impact.impactSpeed;
    this.hitFlash = true;
  }

  /** 1 = undamaged. Exposed for the HUD. */
  get damageScale(): number {
    return this.damage;
  }

  /** True while the reader is backing up, for FX/HUD. */
  reversing = false;

  /** How many consecutive near-standstill ticks the brake has been held. */
  private stoppedTicks = 0;

  /**
   * Hold the brake at a standstill to reverse. Rather than a separate key, it
   * reuses the brake input: braking naturally brings you to a stop, and keeping
   * the pedal buried past that point shifts the kart into a gentle reverse.
   *
   * Deliberately derived here from (brake, speed) rather than a recorded input,
   * so the replay/trace wire contract is unchanged — a replayed trace of the
   * same brake bytes hits the same standstill and reverses the same way.
   */
  private updateReverse(): void {
    const speed = speedOf(this.kart);
    const nearStop = speed < 1.2;
    const brakeHeld = this.controls.brake > 0.35;

    if (this.reversing) {
      // Come out of reverse the moment the pedal is released, or once we're
      // moving forward over the recover threshold.
      if (!brakeHeld || speed > 1.6) this.reversing = false;
    } else if (nearStop && brakeHeld) {
      // A genuine foot-down-at-the-line doesn't reverse anyone — require the
      // brake to have been at or near a stop for a beat first, so braking to
      // the flag never backs you up across the line.
      this.stoppedTicks++;
      if (this.stoppedTicks >= 30) this.reversing = true;
    } else {
      this.stoppedTicks = 0;
    }

    this.controls.reverse = this.reversing ? clamp(this.controls.brake, 0, 1) : 0;
  }

  private placeAt(index: number, speed: number): void {
    const n = this.track.center.length;
    const p = this.track.center[index];
    const ahead = this.track.center[wrapIndex(index + 1, n)];
    const behind = this.track.center[wrapIndex(index - 1, n)];
    const heading = Math.atan2(ahead.y - behind.y, ahead.x - behind.x);

    this.kart.x = p.x;
    this.kart.y = p.y;
    this.kart.heading = heading;
    this.kart.vx = Math.cos(heading) * speed;
    this.kart.vy = Math.sin(heading) * speed;
    this.kart.yawRate = 0;
    this.kart.steer = 0;
    this.kart.locked = false;
    this.kart.frontLoad = 0;
    this.kart.rearLoad = 0;
    this.kart.slip = 0;
    this.kart.lastLongAccel = 0;
    this.prevIndex = index;
    this.currentIndex = index;
    this.drillPrevIndex = index;
  }

  private clearRun(): void {
    this.trace = [];
    this.dragTrace = [];
    this.ticks = 0;
    this.hitCheckpoints.clear();
    this.sectorTicks = [];
    this.nextSector = 1;
    this.microTicks = [];
    this.nextMicro = 1;
    this.maxSpeed = 0;
    this.speedAt.fill(0);
    this.lockedAt.fill(0);
    this.offAt.fill(0);
    this.ghostX = [];
    this.ghostY = [];
    this.ghostH = [];
    this.ghostV = [];
    this.ghostB = [];
    this.ghostS = [];
    this.ghostLatG = [];
    this.ghostLongG = [];
    this.ghostFront = [];
    this.ghostRear = [];
    this.ghostIdx = [];
    this.prevVx = 0;
    this.prevVy = 0;
    this.respawns = 0;
    this.lastCheckpoint = this.track.startIndex;
    this.result = null;
    this.onTrack = true;
    this.damage = 1;
    this.impact = null;
    this.hitFlash = false;
    this.damageAccumulated = 0;
    this.reversing = false;
    this.stoppedTicks = 0;
    this.tyres.temp = 0;
    this.tyres.wear = 0;
    this.phase = 'idle';
  }

  /**
   * @param rollingStart Launch already at the ideal lap's own speed at the
   *   line, rather than from rest. Kept for API completeness; the game now
   *   always starts from a standstill (see `beginRun`), because a rolling
   *   start threw the kart into turn one at full pace with no run-up.
   */
  reset(rollingStart = false): void {
    this.drill = null;
    this.drillResult = null;
    this.drillBestMs = null;
    this.drillAttempts = 0;
    this.holdTicks = 0;
    this.clearRun();
    const speed = rollingStart ? this.profile.vIdeal[this.track.startIndex] : 0;
    this.placeAt(this.track.startIndex, speed);
  }

  /**
   * Start (or restart) a corner drill.
   *
   * Keeps `drillBestMs` and the attempt count across retries — that continuity
   * is the whole point of drilling, and rebuilding the session each attempt
   * would throw it away.
   */
  startDrill(segment: DrillSegment, holdSeconds = HOLD_SECONDS): void {
    const carryBest = this.drill?.corner.number === segment.corner.number &&
      this.drill.mode === segment.mode
      ? this.drillBestMs
      : null;
    const carryAttempts = carryBest === null ? 0 : this.drillAttempts;

    this.clearRun();
    this.drill = segment;
    this.drillResult = null;
    this.drillBestMs = carryBest;
    this.drillAttempts = carryAttempts;
    this.holdTicks = Math.round(holdSeconds / TICK_DT);

    this.placeAt(segment.startIndex, this.profile.vIdeal[segment.startIndex]);
    // Respawn must return to the drill, not the start line. `clearRun` points
    // this at the start/finish, which is what made Backspace teleport you to the
    // opposite end of the circuit.
    this.lastCheckpoint = segment.startIndex;
    // Drill tyres start warm; the point is the corner, not the out-lap.
    if (this.tyreModel) this.tyres.temp = 1;
    this.phase = 'running';
  }

  /**
   * Starts the lap. `holdSeconds` freezes physics and the clock first, same as
   * a drill's countdown — Grand Prix uses it for the start-lights sequence;
   * every other caller omits it and launches immediately, unchanged.
   */
  launch(holdSeconds = 0): void {
    if (this.phase !== 'idle') return;
    this.phase = 'running';
    this.holdTicks = Math.round(holdSeconds / TICK_DT);
  }

  get elapsedMs(): number {
    if (this.phase === 'finished' && this.result) return this.result.timeMs;
    return this.ticks * TICK_DT * 1000;
  }

  get progress(): number {
    return this.hitCheckpoints.size / this.track.checkpoints.length;
  }

  get liveControls(): Controls {
    return this.controls;
  }

  /** Cumulative sector times so far, ms. */
  get sectorMs(): number[] {
    return this.sectorTicks.map((t) => t * TICK_DT * 1000);
  }

  /** Cumulative microsector times so far, ms. */
  get microMs(): number[] {
    return this.microTicks.map((t) => t * TICK_DT * 1000);
  }

  /** Grip at one point, folding surface, weather and tyre state together. */
  private gripAt(index: number, surfaceName: Surface['name']): GripPatch {
    const surface = SURFACES[surfaceName];

    // Weather only wets the racing surface; everything else's penalty is
    // already baked into its own fixed mu.
    const base = surfaceName === 'track' ? this.weather.muAt[index] : surface.mu;
    const mu = this.tyreModel ? base * tyreGrip(this.tyres) : base;
    return { mu, extraDrag: surface.extraDrag };
  }

  /**
   * Grip under the whole kart, not just its centre.
   *
   * The physics tracks one point (`kart.x, kart.y`), but the kart itself has
   * width — sampling only that point means half the vehicle can hang over the
   * grass for free, and running two wheels off produces no consequence at all
   * until the centre itself crosses the line. Sampling both edges too and
   * averaging the three grips is what makes a graze cost *something* rather
   * than nothing, right up to the point the whole kart is off.
   */
  private gripHere(
    kart: KartState,
  ): { grip: GripPatch; index: number; onTrack: boolean; kerbFraction: number } {
    // `currentIndex` is where this kart resolved last tick and is refreshed
    // unconditionally below, so it is a truthful continuity hint even for a
    // kart that is reversing. Without it, all three probes are free to answer
    // with whichever ribbon happens to be nearest, and on Suzuka's flattened
    // crossover that is the other half of the lap — which reads as grass under
    // a kart that is on the racing line. See TrackQuery.sample.
    const hint = this.currentIndex;
    const centre = this.query.sample(kart.x, kart.y, hint);
    const px = -Math.sin(kart.heading);
    const py = Math.cos(kart.heading);
    const hw = VEHICLE_HALF_WIDTH;
    const left = this.query.sample(kart.x + px * hw, kart.y + py * hw, hint);
    const right = this.query.sample(kart.x - px * hw, kart.y - py * hw, hint);

    const gC = this.gripAt(centre.index, centre.surface.name);
    const gL = this.gripAt(left.index, left.surface.name);
    const gR = this.gripAt(right.index, right.surface.name);

    // Anything beyond the kerb counts as off, including a real circuit's
    // paved run-off — full-grip asphalt is still off the marked track, and
    // this is the one place that stays true even though the tyres cannot
    // tell the difference. See docs/circuit-fidelity.md §6.2.
    const isOnSurface = (name: Surface['name']): boolean => name === 'track' || name === 'kerb';
    const onTrack = isOnSurface(centre.surface.name) && isOnSurface(left.surface.name) && isOnSurface(right.surface.name);
    const kerbFraction =
      [centre, left, right].filter((s) => s.surface.name === 'kerb').length / 3;

    const mu = (gC.mu + gL.mu + gR.mu) / 3;
    this.currentMu = mu;
    return {
      grip: { mu, extraDrag: (gC.extraDrag + gL.extraDrag + gR.extraDrag) / 3 },
      index: centre.index,
      onTrack,
      kerbFraction,
    };
  }

  /** Returns true on the tick the lap — or the drill — completes. */
  tick(steerByte: number, brakeByte: number, dragScale = 1): boolean {
    if (this.phase !== 'running') return false;

    // The hold freezes physics and the clock while the countdown runs, so you
    // get time to read the corner without it costing you anything.
    if (this.holdTicks > 0) {
      this.holdTicks--;
      return false;
    }

    const dragByte = quantiseDrag(dragScale);
    const drag = dequantiseDrag(dragByte);

    this.trace.push(steerByte, brakeByte);
    this.dragTrace.push(dragByte);
    dequantise(steerByte, brakeByte, this.controls);
    this.updateReverse();

    const before = { x: this.kart.x, y: this.kart.y };
    const { grip, index, onTrack, kerbFraction } = this.gripHere(this.kart);
    this.onTrack = onTrack;
    this.currentIndex = index;
    this.currentKerb = kerbFraction;

    this.prevVx = this.kart.vx;
    this.prevVy = this.kart.vy;
    stepKart(this.kart, this.controls, grip, this.realism, TICK_DT, drag, this.damage);
    this.impact = clampToBarrier(this.track, this.query, this.kart, this.currentIndex);
    if (this.impact) this.applyBarrierDamage(this.impact);
    this.ticks++;

    // Kerbs are more than a mu/drag patch — real ones unsettle the chassis. A
    // deterministic wobble (not noise: replay must reproduce bit-for-bit)
    // driven by the tick count and how much of the kart is actually on the
    // kerb, oscillating rather than biased so it chatters the kart rather
    // than steering it.
    if (kerbFraction > 0) {
      const speedScale = Math.min(1, speedOf(this.kart) / 15);
      this.kart.yawRate += Math.sin(this.ticks * 2.4) * kerbFraction * speedScale * KERB_RUMBLE_GAIN;
    }

    const speed = speedOf(this.kart);
    this.maxSpeed = Math.max(this.maxSpeed, speed);

    if (this.tyreModel) {
      const load = Math.max(this.kart.frontLoad, this.kart.rearLoad);
      stepTyres(this.tyres, Math.abs(this.kart.slip), load, TICK_DT);
    }

    // First visit to each centerline sample is what the analysis compares
    // against the ideal profile.
    if (this.speedAt[index] === 0) this.speedAt[index] = speed;
    if (this.kart.locked) this.lockedAt[index] = 1;
    if (!onTrack) this.offAt[index] = 1;

    this.advanceProgress(index);

    // Split this tick's acceleration along and across the direction of
    // travel. Resolving against velocity rather than heading is what makes it
    // a real g-reading: during a slide the kart is not pointing where it is
    // going, and the forces follow the motion, not the nose.
    //
    // Computed every tick, not just on the ghost's own stride, so the HUD's
    // live g-readout is not a quarter-tick stale during the exact moments —
    // a lock-up, a snap — it would matter most to see it immediately.
    const ax = (this.kart.vx - this.prevVx) / TICK_DT;
    const ay = (this.kart.vy - this.prevVy) / TICK_DT;
    const mag = Math.hypot(this.prevVx, this.prevVy);
    if (mag > 0.5) {
      const ux = this.prevVx / mag;
      const uy = this.prevVy / mag;
      this.liveLongG = (ax * ux + ay * uy) / G;
      this.liveLatG = (ax * -uy + ay * ux) / G;
    } else {
      this.liveLongG = 0;
      this.liveLatG = 0;
    }

    if (this.ticks % GHOST_STRIDE === 0) {
      this.ghostX.push(this.kart.x);
      this.ghostY.push(this.kart.y);
      this.ghostH.push(this.kart.heading);
      this.ghostV.push(speed);
      this.ghostB.push(this.controls.brake);
      this.ghostS.push(this.controls.steer);
      this.ghostFront.push(this.kart.frontLoad);
      this.ghostRear.push(this.kart.rearLoad);
      this.ghostIdx.push(index);
      this.ghostLongG.push(this.liveLongG);
      this.ghostLatG.push(this.liveLatG);
    }

    if (this.drill) return this.checkDrillFinish(index);
    return this.checkFinish(before);
  }

  /**
   * A drill ends at its own finish line, not the start/finish straight.
   *
   * Walking the span from the previous sample rather than testing one index
   * means a fast kart cannot skip past the end between ticks — the same reason
   * checkpoints are handled that way.
   */
  private checkDrillFinish(cur: number): boolean {
    const drill = this.drill;
    if (!drill) return false;
    const n = this.track.center.length;

    let steps = wrapIndex(cur - this.drillPrevIndex, n);
    if (steps > n / 2) {
      this.drillPrevIndex = cur;
      return false; // moved backwards
    }
    if (steps > 60) steps = 60;

    let crossed = false;
    for (let s = 1; s <= steps; s++) {
      if (wrapIndex(this.drillPrevIndex + s, n) === drill.endIndex) {
        crossed = true;
        break;
      }
    }
    this.drillPrevIndex = cur;
    if (!crossed) return false;

    this.finishDrill(drill);
    return true;
  }

  private finishDrill(drill: DrillSegment): void {
    this.phase = 'finished';
    this.drillAttempts++;
    const timeMs = this.ticks * TICK_DT * 1000;

    // Score the stretch between the braking point and the exit — the part the
    // drill is actually about, not the run-up.
    const n = this.track.center.length;
    let vMin = Infinity;
    let vMinIdeal = Infinity;
    let locked = false;
    let wentOff = false;

    let i = drill.brakeIndex;
    for (let guard = 0; guard < n; guard++) {
      const v = this.speedAt[i];
      if (v > 0.1) vMin = Math.min(vMin, v);
      vMinIdeal = Math.min(vMinIdeal, this.profile.vIdeal[i]);
      if (this.lockedAt[i]) locked = true;
      if (this.offAt[i]) wentOff = true;
      if (i === drill.corner.exitIndex) break;
      i = wrapIndex(i + 1, n);
    }

    // Where braking actually began: the fastest point on the approach.
    let playerBrake = drill.apexIndex;
    let peak = -1;
    let scan = drill.apexIndex;
    const searchSteps = wrapIndex(drill.apexIndex - drill.startIndex, n);
    for (let s = 0; s < searchSteps; s++) {
      scan = wrapIndex(scan - 1, n);
      const v = this.speedAt[scan];
      if (v < 0.1) continue;
      if (v > peak) {
        peak = v;
        playerBrake = scan;
      }
    }
    let offsetSteps = wrapIndex(playerBrake - drill.brakeIndex, n);
    if (offsetSteps > n / 2) offsetSteps -= n;

    const personalBest = this.drillBestMs === null || timeMs < this.drillBestMs;
    if (personalBest) this.drillBestMs = timeMs;

    this.drillResult = {
      timeMs,
      idealMs: drill.idealMs,
      vMin: vMin === Infinity ? 0 : vMin,
      vMinIdeal,
      brakeOffset: offsetSteps * this.track.spacing,
      locked,
      wentOff,
      personalBest,
    };
  }

  /**
   * Mark every checkpoint and sector boundary between the previous sample and
   * this one. Walking the span rather than testing a single index means a fast
   * kart cannot skip one between ticks.
   */
  private advanceProgress(cur: number): void {
    // Checkpoints and sectors are lap concepts. A drill crosses them incidentally
    // and the splits it would record are meaningless, so it must not accumulate
    // any of it — otherwise drill state leaks into the lap's.
    if (this.drill) {
      this.prevIndex = cur;
      return;
    }

    const n = this.track.center.length;
    let steps = wrapIndex(cur - this.prevIndex, n);
    if (steps > n / 2) return; // moved backwards; ignore
    if (steps > 40) steps = 40;

    for (let s = 1; s <= steps; s++) {
      const idx = wrapIndex(this.prevIndex + s, n);
      // A checkpoint only counts if the kart is on the racing surface as it
      // passes — otherwise cutting across the grass would still collect it
      // and validate the lap.
      if (this.checkpointSet.has(idx) && this.onTrack) {
        this.hitCheckpoints.add(idx);
        this.lastCheckpoint = idx;
      }
      if (this.nextSector < 3 && idx === this.track.sectorStarts[this.nextSector]) {
        this.sectorTicks.push(this.ticks);
        this.nextSector++;
      }
      if (
        this.nextMicro < this.track.microStarts.length &&
        idx === this.track.microStarts[this.nextMicro]
      ) {
        this.microTicks.push(this.ticks);
        this.nextMicro++;
      }
    }
    this.prevIndex = cur;
  }

  private checkFinish(before: Vec2): boolean {
    // Half the checkpoints is enough to rule out the crossing that happens on
    // the very first tick, sitting on the line at a standstill.
    if (this.progress < 0.5) return false;

    const after = { x: this.kart.x, y: this.kart.y };
    const t = segmentCrossing(before, after, this.line.a, this.line.b);
    if (t === null) return false;

    const dx = after.x - before.x;
    const dy = after.y - before.y;
    if (dx * this.forward.x + dy * this.forward.y <= 0) return false;

    this.finish(t);
    return true;
  }

  private finish(subTick: number): void {
    this.phase = 'finished';

    // Sub-tick precision. At 25 m/s one whole tick is 21 cm of track, easily
    // enough to reorder a leaderboard.
    const timeMs = (this.ticks - 1 + subTick) * TICK_DT * 1000;

    const hit = this.hitCheckpoints.size;
    const total = this.track.checkpoints.length;
    const valid = hit / total >= CHECKPOINT_FRACTION;

    const deltas = analyseRun(
      this.track,
      this.profile,
      this.corners,
      this.speedAt,
      this.lockedAt,
      this.offAt,
    );
    let worst: CornerDelta | null = null;
    for (const d of deltas) if (!worst || d.lost > worst.lost) worst = d;

    this.result = {
      timeMs,
      valid,
      reason: valid ? '' : `missed ${total - hit} of ${total} checkpoints — corner cutting`,
      checkpointsHit: hit,
      checkpointsTotal: total,
      idealMs: this.profile.idealLapTime * 1000,
      sectorMs: [...this.sectorMs, timeMs],
      microMs: [...this.microMs, timeMs],
      deltas,
      worst,
      ghost: {
        x: Float32Array.from(this.ghostX),
        y: Float32Array.from(this.ghostY),
        heading: Float32Array.from(this.ghostH),
        speed: Float32Array.from(this.ghostV),
        brake: Float32Array.from(this.ghostB),
        steer: Float32Array.from(this.ghostS),
        latG: Float32Array.from(this.ghostLatG),
        longG: Float32Array.from(this.ghostLongG),
        frontLoad: Float32Array.from(this.ghostFront),
        rearLoad: Float32Array.from(this.ghostRear),
        index: Int32Array.from(this.ghostIdx),
        timeMs,
      },
      trace: Uint8Array.from(this.trace),
      dragTrace: Uint8Array.from(this.dragTrace),
      maxSpeed: this.maxSpeed,
      tyreModel: this.tyreModel,
      respawns: this.respawns,
    };
  }

  /**
   * The ghost recorded so far, without needing a completed lap.
   *
   * A drill finishes at its own end marker and never produces a lap `result`,
   * so this is how a drill run hands back the line it drove.
   */
  snapshotGhost(): Ghost {
    return {
      x: Float32Array.from(this.ghostX),
      y: Float32Array.from(this.ghostY),
      heading: Float32Array.from(this.ghostH),
      speed: Float32Array.from(this.ghostV),
      brake: Float32Array.from(this.ghostB),
      steer: Float32Array.from(this.ghostS),
      latG: Float32Array.from(this.ghostLatG),
      longG: Float32Array.from(this.ghostLongG),
      frontLoad: Float32Array.from(this.ghostFront),
      rearLoad: Float32Array.from(this.ghostRear),
      index: Int32Array.from(this.ghostIdx),
      timeMs: this.ticks * TICK_DT * 1000,
    };
  }

  /** Live delta against a reference ghost, in ms. Positive means slower. */
  deltaTo(reference: Ghost | null): number | null {
    if (!reference || reference.x.length === 0 || this.phase !== 'running') return null;
    let best = -1;
    let bestDist = Infinity;
    for (let i = 0; i < reference.x.length; i++) {
      const d = (reference.x[i] - this.kart.x) ** 2 + (reference.y[i] - this.kart.y) ** 2;
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    if (best < 0 || bestDist > 400) return null;
    return this.elapsedMs - best * GHOST_STRIDE * TICK_DT * 1000;
  }
}

/** Interpolated ghost pose at a given time. */
export function ghostPose(
  g: Ghost,
  timeMs: number,
): { x: number; y: number; heading: number; speed: number } | null {
  if (g.x.length === 0) return null;
  const frame = timeMs / (GHOST_STRIDE * TICK_DT * 1000);
  const i = Math.floor(frame);
  if (i >= g.x.length - 1) {
    const last = g.x.length - 1;
    return { x: g.x[last], y: g.y[last], heading: g.heading[last], speed: g.speed[last] };
  }
  const t = frame - i;
  const dh = g.heading[i + 1] - g.heading[i];
  const wrapped = dh > Math.PI ? dh - Math.PI * 2 : dh < -Math.PI ? dh + Math.PI * 2 : dh;
  return {
    x: g.x[i] + (g.x[i + 1] - g.x[i]) * t,
    y: g.y[i] + (g.y[i + 1] - g.y[i]) * t,
    heading: g.heading[i] + wrapped * t,
    speed: g.speed[i] + (g.speed[i + 1] - g.speed[i]) * t,
  };
}

/** Number of physics ticks stored in a trace. */
export function traceTicks(trace: Uint8Array): number {
  return Math.floor(trace.length / TRACE_STRIDE);
}
